import React, { useEffect, useState } from "react";
import { Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { ColumnConfig } from "../types/ColumnConfig";
import { TablePagination } from "../types/Pagination";
import ConfirmRedemption from "../components/ConfirmRedemption";
import DateInput from "../components/DateInput";
import { formatToDDMMYYYY } from "../utils/Validation";
import "./Redemption.css";
import "./Common.css";
import "rsuite/dist/rsuite.min.css";
import DropdownSearch from "../components/DropDown";
import { getRedemptionList, getUserListForFilters, initiateBankTransfer } from "../services/apiService";
import ExporterButton from "../components/ExportButton";
import TransactionDetailsModal from "../components/TransactionDetailsModal";

const Redemption = () => {
  const [filters, setFilters] = useState({
    userId: "",
    mobile: "",
    status: "pending",
    paymentMode: "",
    fromDate: null,
    toDate: null,
  });

  const dataDropdown = [
    { label: "Pending", value: "pending" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
  ];

  const statusDropdown = [
    { label: "Bank Transfer", value: "bank_transfer" },
    { label: "UPI", value: "upi" },
  ];

  const [data, setData] = useState<any[]>([]);
  const [paginationData, setPaginationData] = useState<TablePagination>({
    limit: 10,
    skip: 0,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [openDialog, setOpenDialog] = useState({ open: false, action: "" });
  const [redemeptionRefList, setRedemeptionRefList] = useState<number[]>([]);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [userList, setUserList] = useState<{ userId: number; userName: string }[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [selectedRedemptionId, setSelectedRedemptionId] = useState<number | null>(null);
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);

  const selectedTransaction = data.find((r) => r.id === selectedRedemptionId)?.bankTransaction;

  useEffect(() => {
    const fetchUserList = async () => {
      setUserLoading(true);
      try {
        const response = await getUserListForFilters();
        if (response?.data) {
          setUserList(response?.data);
        }
      } catch (error) {
        console.error("Error fetching user list", error);
        toast.error("Failed to load user list");
      } finally {
        setUserLoading(false);
      }
    };
    fetchUserList();

    const handler = setTimeout(() => {
      const isUserIdValid = filters.userId === "" || filters.userId?.length >= 1;
      const areDatesValid =
        (filters.fromDate && filters.toDate) ||
        (!filters.fromDate && !filters.toDate);

      if (isUserIdValid && areDatesValid) {
        loadData();
      }
    }, 800);
    return () => clearTimeout(handler);
  }, [filters, paginationData]);

  const userDropdownOptions = userList.map((user) => ({
    label: user.userName,
    value: user.userId.toString(),
  }));

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleInitiateTransfer = async (row: any) => {
    try {
      setLoading(true);
      const response = await initiateBankTransfer(row.id);
      // Assuming response has some form of success indicating message
      toast.success(response?.message || "Transfer initiated successfully");
      loadData();
    } catch (error: any) {
      console.error("Error initiating transfer", error);
      toast.error(error?.response?.data?.message || "Failed to initiate transfer");
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const apiParams: any = {};

      if (filters.userId.trim()) {
        const userIdsArray = filters.userId.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
        if (userIdsArray?.length === 1) {
          apiParams.userId = `[${userIdsArray[0]}]`;
        } else if (userIdsArray?.length > 1) {
          apiParams.userId = `[${userIdsArray.join(',')}]`;
        }
      }

      if (filters.paymentMode) {
        apiParams.mode = filters.paymentMode;
      }

      if (filters.status) {
        apiParams.status = filters.status;
      }

      if (filters.fromDate) {
        apiParams.fromDate = formatToDDMMYYYY(filters.fromDate);
      }
      if (filters.toDate) {
        apiParams.toDate = formatToDDMMYYYY(filters.toDate);
      }

      const response = await getRedemptionList(apiParams);
      if (response?.data) {
        setData(response?.data);
        setTotalRows(response?.totalCount || response?.data?.length);
      } else {
        setData([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error("Error loading redemption data", error);
      toast.error("Error loading redemption data");
      setData([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnConfig[] = [
    {
      field: "fullName",
      headerName: "Name",
      width: 160,
      editable: false,
      sortable: false,
      headerClassName: "custom-header",
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: "points",
      headerName: "Points Redeemed",
      width: 160,
      editable: false,
      sortable: false,
      headerClassName: "custom-header",
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: "redemptionStatus",
      headerName: "Status",
      width: 120,
      editable: false,
      sortable: false,
      headerClassName: "custom-header",
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const status = params.value?.toLowerCase();
        return (
          <span style={{
            padding: '4px 8px',
            borderRadius: '4px',
            backgroundColor:
              status === 'pending' ? '#fff3cd' :
                status === 'approved' ? '#d4edda' :
                  status === 'rejected' ? '#f8d7da' : 'transparent',
            color:
              status === 'pending' ? '#856404' :
                status === 'approved' ? '#155724' :
                  status === 'rejected' ? '#721c24' : 'inherit',
            fontSize: '12px',
            fontWeight: '500'
          }}>
            {params.value?.charAt(0).toUpperCase() + params.value?.slice(1)}
          </span>
        );
      }
    },
    {
      field: "redemptionMode",
      headerName: "Redemption Mode",
      width: 180,
      editable: false,
      sortable: false,
      headerClassName: "custom-header",
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const mode = params.value?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
        return mode || '-';
      }
    },
    {
      field: "accountNumber",
      headerName: "Account Number",
      width: 180,
      editable: false,
      sortable: false,
      headerClassName: "custom-header",
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        return params.value || '-';
      }
    },
    {
      field: "ifscCode",
      headerName: "IFSC Code",
      width: 180,
      editable: false,
      sortable: false,
      headerClassName: "custom-header",
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        return params.value || '-';
      }
    },
    {
      field: "accountType",
      headerName: "Account Type",
      width: 180,
      editable: false,
      sortable: false,
      headerClassName: "custom-header",
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        return params.value || '-';
      }
    },
    {
      field: "accountHolderName",
      headerName: "Account Holder Name",
      width: 180,
      editable: false,
      sortable: false,
      headerClassName: "custom-header",
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        return (
          <div style={{
            maxWidth: '170px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }} title={params.value || ''}>
            {params.value || '-'}
          </div>
        );
      }
    },
    {
      field: "upiId",
      headerName: "UPI ID",
      width: 160,
      editable: false,
      sortable: false,
      headerClassName: "custom-header",
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        return (
          <div style={{
            maxWidth: '170px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }} title={params.value || ''}>
            {params.value || '-'}
          </div>
        );
      }
    },
    {
      field: "createdAt",
      headerName: "Upload Date",
      width: 180,
      editable: false,
      sortable: false,
      headerClassName: "custom-header",
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        return params.value ? formatToDDMMYYYY(params.value) : '-';
      }
    },
    {
      field: "action",
      headerName: "Action",
      width: 200,
      editable: false,
      sortable: false,
      headerClassName: "custom-header",
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const hasTransaction = !!params.row.bankTransaction;
        
        return hasTransaction ? (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            onClick={() => {
              console.log("View transfer details for:", params.row);
              setSelectedRedemptionId(params.row.id);
              setIsTransactionModalOpen(true);
            }}
          >
            VIEW TRANSFER DETAILS
          </Button>
        ) : (
          '-'
          /*
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleInitiateTransfer(params.row)}
          >
            INITIATE TRANSFER
          </Button>
          */
        );
      }
    },
  ];

  const handlePaginationChange = async (e: any) => {
    const paginationData: TablePagination = {
      limit: e.pageSize,
      skip: e.page * e.pageSize,
    };
    setPaginationData(paginationData);
  };

  const handleInput = (e: string, type: string) => {
    setFilters((prev) => ({ ...prev, [type]: e }));
    if (type !== 'limit' && type !== 'skip') {
      setPaginationData({ limit: 10, skip: 0 });
    }
  };

  const handleDateInput = (date: any, type: string) => {
    setFilters((prev) => ({ ...prev, [type]: date }));
    setPaginationData({ limit: 10, skip: 0 });
  };

  const handleSelectionChange = (a: any) => {
    setRedemeptionRefList(a);
  };

  const handleConfirmationPopup = (type: string = "cancel", message?: string) => {
    if (type === "success") {
      loadData();
      setRedemeptionRefList([]);
    }
    if (type === "cancel") {
      setRedemeptionRefList([]);
    }
    if (message) {
      if (type === "success") {
        toast.success(message);
      } else {
        toast.error(message);
      }
    }
    setOpenDialog((prev) => ({ ...prev, open: false }));
  };

  const submit = async (type: string) => {
    try {
      if (redemeptionRefList?.length) {
        const filteredData = data.filter((item) => redemeptionRefList.includes(item.id));
        setPreviewData(filteredData);
        setOpenDialog({ open: true, action: type });
      }
    } catch (error) {
      console.error("Error submitting redemption", error);
      toast.error("Error submitting redemption");
    }
  };

  const fileExporter = async () => {
    try {
      const apiParams: any = {};

      if (filters.userId.trim()) {
        const userIdsArray = filters.userId.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
        if (userIdsArray?.length === 1) {
          apiParams.userId = `[${userIdsArray[0]}]`;
        } else if (userIdsArray?.length > 1) {
          apiParams.userId = `[${userIdsArray.join(',')}]`;
        }
      }

      if (filters.paymentMode) {
        apiParams.mode = filters.paymentMode;
      }

      if (filters.status) {
        apiParams.status = filters.status;
      }

      if (filters.fromDate) {
        apiParams.fromDate = formatToDDMMYYYY(filters.fromDate);
      }
      if (filters.toDate) {
        apiParams.toDate = formatToDDMMYYYY(filters.toDate);
      }

      apiParams.limit = totalRows || 10000;
      apiParams.skip = 0;

      const response = await getRedemptionList(apiParams);
      const exportData = response?.data;
      if (exportData && exportData.length > 0) {
        toast.success("Exported successfully.");
        return exportData;
      } else {
        toast.error("No data available to export.");
        return [];
      }
    } catch (error) {
      toast.error("Failed to fetch export data.");
      return [];
    }
  };

  return (
    <div>
      <div
        style={{
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <span className="redemption-title">Approve / Reject Redemption</span>
        <div
          className="redemption-header"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            width: "100%",
          }}
        >
          <div style={{ display: "flex", gap: "50px", flexWrap: "wrap" }}>
            <DropdownSearch
              searchTxt={filters.userId}
              handleChange={(e: any) => handleInput(e, "userId")}
              placeholder="Select User"
              label="User Search"
              data={userDropdownOptions}
              searchable={true}
              cleanable={true}
            />
            <DropdownSearch
              searchTxt={filters.paymentMode}
              handleChange={(e: any) => handleInput(e, "paymentMode")}
              placeholder="Select Payment Mode"
              label="Payment Mode"
              data={statusDropdown}
              searchable={true}
              cleanable={true}
            />
            <DateInput
              handleInput={(date) => handleDateInput(date, "fromDate")}
              searchTxt={filters.fromDate}
              placeholder="Select From Date"
              label="From Date"
              minDate={filters.toDate}
            />
            <DateInput
              handleInput={(date) => handleDateInput(date, "toDate")}
              searchTxt={filters.toDate}
              placeholder="Select To Date"
              label="To Date"
              maxDate={filters.fromDate}
            />
          </div>
        </div>

        <div className="btn-group">
          <Button
            variant="contained"
            color="success"
            onClick={() => submit("Approve")}
            disabled={redemeptionRefList?.length === 0 || loading}
            sx={{ opacity: redemeptionRefList?.length === 0 ? 0.5 : 1 }}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => submit("Reject")}
            disabled={redemeptionRefList?.length === 0 || loading}
            sx={{ opacity: redemeptionRefList?.length === 0 ? 0.5 : 1 }}
          >
            Reject
          </Button>
        </div>

        <div className="table-container">
          <div style={{ display: "flex", justifyContent: "flex-start", padding: "10px 10px 0px 10px" }}>
            <ExporterButton exporter={fileExporter} reportName="Approve / Reject Redemption" />
          </div>
          <DataGrid
            rows={data}
            columns={columns}
            onPaginationModelChange={handlePaginationChange}
            autoHeight={true}
            disableColumnFilter={true}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            getRowId={(ele: any) => ele?.id}
            onRowSelectionModelChange={handleSelectionChange}
            rowSelectionModel={redemeptionRefList}
            disableRowSelectionOnClick
            checkboxSelection
            keepNonExistentRowsSelected={true}
            rowCount={totalRows}
            // paginationMode="server"
            loading={loading}
            sx={{
              "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                outline: "none !important",
              },
            }}
          />
        </div>

        <ConfirmRedemption
          open={openDialog.open}
          handleClose={handleConfirmationPopup}
          previewData={previewData}
          action={openDialog.action}
        />

        <TransactionDetailsModal 
          open={isTransactionModalOpen}
          onClose={() => {
            setIsTransactionModalOpen(false);
            setSelectedRedemptionId(null);
          }}
          transaction={selectedTransaction}
          onRefresh={loadData}
        />
      </div>
    </div>
  );
};

export default Redemption;